import { useEffect, useMemo, useState } from "react";

const LS_VISITS = "pwa_install_banner_visits";
const LS_LAST_DAY = "pwa_install_banner_last_day";
const LS_DISMISSED = "pwa_install_banner_dismissed";
const SHOW_AFTER_VISITS = 1; // állítsd 2-re, ha a 2. látogatástól kell

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function todayKey() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [eligibleByVisits, setEligibleByVisits] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const standalone = useMemo(() => {
    if (typeof window === "undefined") return false;
    return isInStandaloneMode();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isDismissed = localStorage.getItem(LS_DISMISSED) === "1";
    setDismissed(isDismissed);

    if (standalone || isDismissed) return;

    // Visit counter: naponta max 1-gyel növeljük (ne számoljon refresh-től)
    const lastDay = localStorage.getItem(LS_LAST_DAY);
    const today = todayKey();
    let visits = parseInt(localStorage.getItem(LS_VISITS) || "0", 10);

    if (lastDay !== today) {
      visits += 1;
      localStorage.setItem(LS_VISITS, String(visits));
      localStorage.setItem(LS_LAST_DAY, today);
    }

    setEligibleByVisits(visits >= SHOW_AFTER_VISITS);

    // iOS: nincs beforeinstallprompt, csak hint
    if (isIos()) setShowIosHint(true);

    // Chromium: elkapjuk a promptot
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setShowIosHint(false);
      // ha telepítette, ne zaklassuk később sem
      localStorage.setItem(LS_DISMISSED, "1");
      setDismissed(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [standalone]);

  const dontShowAgain = () => {
    localStorage.setItem(LS_DISMISSED, "1");
    setDismissed(true);
  };

  if (standalone || dismissed) return null;
  if (!eligibleByVisits) return null;

  // Android/Chrome/Edge install gomb
  if (deferredPrompt) {
    return (
      <div className="alert alert-primary d-flex align-items-center justify-content-between m-3">
        <div>
          <strong>Telepítsd alkalmazásként</strong>
          <div className="small">Gyorsabban elérhető, ikonról indítható.</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={dontShowAgain}>
            Ne mutasd többet
          </button>
          <button
            className="btn btn-primary"
            onClick={async () => {
              deferredPrompt.prompt();
              await deferredPrompt.userChoice;
              setDeferredPrompt(null); // a prompt csak egyszer használható
            }}
          >
            Telepítés
          </button>
        </div>
      </div>
    );
  }

  // iOS (Safari): csak útmutató
  if (showIosHint) {
    return (
      <div className="alert alert-secondary d-flex align-items-center justify-content-between m-3">
        <div>
          <strong>iPhone/iPad telepítés</strong>
          <div className="small">
            Safari-ban nyomd meg a <b>Megosztás</b> gombot, majd <b>Add to Home Screen</b>.
          </div>
        </div>
        <button className="btn btn-outline-secondary" onClick={dontShowAgain}>
          Ne mutasd többet
        </button>
      </div>
    );
  }

  return null;
}
